import React, { useState, useEffect, useRef } from 'react';

const FILLER_TYPES = [
    { key: 'soft', label: 'Agex Fill Soft', color: '#00BFFF' },
    { key: 'volume', label: 'Agex Fill Volume', color: '#B07CC6' },
    { key: 'ultra', label: 'Agex Fill Ultra', color: '#FF8C00' }
];

const MappaFiller = ({ data, onChange, readOnly }) => {
    const [valori, setValori] = useState(data?.valori || {});
    const [zones, setZones] = useState(data?.zones || []);
    const [activeFiller, setActiveFiller] = useState('soft');
    const [isDrawing, setIsDrawing] = useState(false);
    const [drawStart, setDrawStart] = useState(null);
    const [currentDraw, setCurrentDraw] = useState(null);
    const containerRef = useRef(null);

    useEffect(() => {
        if (data) {
            setValori(data.valori || {});
            setZones(data.zones || []);
        }
    }, [data]);

    const getRelativePos = (e) => {
        const rect = containerRef.current.getBoundingClientRect();
        return {
            x: ((e.clientX - rect.left) / rect.width) * 100,
            y: ((e.clientY - rect.top) / rect.height) * 100
        };
    };

    const handleMouseDown = (e) => {
        if (readOnly) return;
        e.preventDefault();
        const pos = getRelativePos(e);
        setIsDrawing(true);
        setDrawStart(pos);
        setCurrentDraw({ x: pos.x, y: pos.y, w: 0, h: 0 });
    };

    const handleMouseMove = (e) => {
        if (!isDrawing || readOnly) return;
        const pos = getRelativePos(e);
        const x = Math.min(drawStart.x, pos.x);
        const y = Math.min(drawStart.y, pos.y);
        const w = Math.abs(pos.x - drawStart.x);
        const h = Math.abs(pos.y - drawStart.y);
        setCurrentDraw({ x, y, w, h });
    };

    const handleMouseUp = () => {
        if (!isDrawing || readOnly) return;
        setIsDrawing(false);
        if (currentDraw && currentDraw.w > 2 && currentDraw.h > 2) {
            const newZone = {
                ...currentDraw,
                id: Date.now(),
                filler: activeFiller
            };
            const newZones = [...zones, newZone];
            setZones(newZones);
            if (onChange) onChange({ valori, zones: newZones });
        }
        setDrawStart(null);
        setCurrentDraw(null);
    };

    const handleRemoveZone = (id) => {
        if (readOnly) return;
        const newZones = zones.filter(z => z.id !== id);
        setZones(newZones);
        if (onChange) onChange({ valori, zones: newZones });
    };

    const handleValoreChange = (key, value) => {
        const newValori = { ...valori, [key]: value };
        setValori(newValori);
        if (onChange) onChange({ valori: newValori, zones });
    };

    const getFillerColor = (key) => {
        const f = FILLER_TYPES.find(t => t.key === key);
        return f ? f.color : '#ccc';
    };

    return (
        <div className="mappa-filler">
            <div
                className="filler-image-container"
                ref={containerRef}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
            >
                <img src="/images/filler-face.png" alt="Mappa Filler" draggable="false" />
                {zones.map((zone) => (
                    <div
                        key={zone.id}
                        className="filler-zone"
                        style={{
                            left: zone.x + '%',
                            top: zone.y + '%',
                            width: zone.w + '%',
                            height: zone.h + '%',
                            borderColor: getFillerColor(zone.filler),
                            backgroundColor: getFillerColor(zone.filler) + '30'
                        }}
                        onClick={(e) => { e.stopPropagation(); handleRemoveZone(zone.id); }}
                        title={readOnly ? FILLER_TYPES.find(t => t.key === zone.filler)?.label : 'Clicca per rimuovere'}
                    />
                ))}
                {currentDraw && currentDraw.w > 0 && (
                    <div
                        className="filler-zone filler-zone-drawing"
                        style={{
                            left: currentDraw.x + '%',
                            top: currentDraw.y + '%',
                            width: currentDraw.w + '%',
                            height: currentDraw.h + '%',
                            borderColor: getFillerColor(activeFiller),
                            backgroundColor: getFillerColor(activeFiller) + '30'
                        }}
                    />
                )}
            </div>
            <div className="filler-fields">
                {!readOnly && (
                    <div className="filler-selector">
                        <label className="filler-selector-label">Seleziona Filler:</label>
                        {FILLER_TYPES.map((tipo) => (
                            <button
                                key={tipo.key}
                                className={'filler-type-btn' + (activeFiller === tipo.key ? ' active' : '')}
                                style={{
                                    borderColor: tipo.color,
                                    backgroundColor: activeFiller === tipo.key ? tipo.color : 'transparent',
                                    color: activeFiller === tipo.key ? '#fff' : tipo.color
                                }}
                                onClick={() => setActiveFiller(tipo.key)}
                            >
                                {tipo.label}
                            </button>
                        ))}
                        <p className="filler-hint">Disegna sull'immagine per creare zone</p>
                    </div>
                )}
                <div className="filler-values-section">
                    {FILLER_TYPES.map((tipo) => (
                        <div key={tipo.key} className="filler-field-row">
                            <div className="filler-color-dot" style={{ backgroundColor: tipo.color }}></div>
                            <label>{tipo.label}</label>
                            {readOnly ? (
                                <span className="filler-field-value">{valori[tipo.key] || '___'}</span>
                            ) : (
                                <input
                                    type="text"
                                    value={valori[tipo.key] || ''}
                                    onChange={(e) => handleValoreChange(tipo.key, e.target.value)}
                                    placeholder="___"
                                />
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default MappaFiller;
